import { BaseMarker } from '../base/baseMarker'
import { PolygonMarkerStateManager } from './polygonMarkerStateManager'
import { PolygonMarkerRenderer } from './polygonMarkerRenderer'
import { PolygonMarkerController } from './polygonMarkerController'
import { PolygonMarkerHandles } from './polygonMarkerHandles'
import { pointToSegmentDistance, polygonContainsPoint } from '../../helperFunctions'
import { PolygonMarkerEdge } from './PolygonMarkerEdge'
import { SquareHandle } from '../handles/squareHandle'

class PolygonMarker extends BaseMarker<
  PolygonMarkerStateManager,
  PolygonMarkerRenderer,
  PolygonMarkerController,
  PolygonMarkerHandles
> {
  constructor() {
    super()
    this.controller = new PolygonMarkerController(this)
    this.renderer = new PolygonMarkerRenderer(this)
    this.state = new PolygonMarkerStateManager(this)
    this.handles = new PolygonMarkerHandles(this)
  }

  public static typeName = 'PolygonMarker'

  getMouseToShapeDistance(): number {
    const controller = this.controller
    const points = this.state.points
    const mouseX = controller.mouseX
    const mouseY = controller.mouseY
    let minDist = Infinity

    points.forEach((vertex, i) => {
      const v1 = vertex
      const v2 = points[(i + 1) % points.length] // Wrap around to the first vertex
      const dist = pointToSegmentDistance(mouseX, mouseY, v1.x, v1.y, v2.x, v2.y)
      if (dist < minDist) {
        minDist = dist
      }
    })
    return minDist
  }

  isMousedOver() {
    const mousePoint = { x: this.controller.mouseX, y: this.controller.mouseY }
    const handleMousedOver = this.handles.getMousedOverHandle() !== undefined
    return polygonContainsPoint(mousePoint, this.state.points) || handleMousedOver
  }

  /**
   * Determines whether the polygon shape is complete by comparing the first
   * and last line segments
   * @returns boolean
   */
  isComplete() {
    const vertices = this.state.vertices
    const edges = this.state.edges

    if (vertices.length < 3) return false //Can't make a polygon with 2 or less vertices

    const firstEdge = edges[0]
    const lastEdge = edges[edges.length - 1]

    return firstEdge.start.x === lastEdge.end.x && firstEdge.start.y === lastEdge.end.y
  }

  updateMousedOverEdge() {
    const state = this.state
    const edges = state.edges
    const handles = state.handles
    const mouseX = this.controller.mouseX
    const mouseY = this.controller.mouseY
    let mousedOverEdge
    const mousedOverHandle = this.handles.getMousedOverHandle()
    const mouseDistanceThreshold = 15

    edges.forEach((edge: PolygonMarkerEdge) => {
      const mouseToEdgeDistance = pointToSegmentDistance(
        mouseX,
        mouseY,
        edge.start.x,
        edge.start.y,
        edge.end.x,
        edge.end.y
      )
      const isMouseCloseToEdge = mouseToEdgeDistance <= mouseDistanceThreshold
      const isEdgeInHoveredState = edge.isHovered === true

      const shouldPopulateMidpointHandle = isMouseCloseToEdge && !isEdgeInHoveredState && !mousedOverHandle

      const shouldRemoveMidpointHandle =
        (!isMouseCloseToEdge && isEdgeInHoveredState) ||
        (mousedOverHandle && edge.midpointHandle && mousedOverHandle !== edge.midpointHandle)

      if (shouldPopulateMidpointHandle) {
        const midpointHandle = new SquareHandle(
          this.p,
          { x: edge.midpoint.x, y: edge.midpoint.y },
          this.state.handleRadius,
          this.state.handleStrokeWeight
        )
        edge.isHovered = true
        edge.midpointHandle = midpointHandle
        this.state.handles.push(midpointHandle)
        mousedOverEdge = edge
      }
      if (shouldRemoveMidpointHandle) {
        const existingMidpointHandleIndex = handles.indexOf(edge.midpointHandle)
        handles.splice(existingMidpointHandleIndex, 1)
        edge.midpointHandle = null
        edge.isHovered = false
      }
    })
    return mousedOverEdge
  }

  setupMarker(): void {
    this.setupStroke()
  }
}

export { PolygonMarker }
